import { clearZapierLeadsContext } from './zapier-leads-context.js';
import { sanitizeCampaignLaunchReadiness } from './campaign-launch-readiness.js';
import { loginHrefForCurrentLocation } from './invite-handoff.js';
import { registrationSettingsBody } from './platform-registration-settings.js';
import { platformZapierSettingsBody } from './platform-zapier-settings.js';

// Fetch + SSE wrapper. Auth uses Bearer tokens stored in localStorage under
// 'lp_token' — same key as the legacy frontend's auth.js. On 401, we redirect
// to /login (proxied from Express in dev, served directly in prod).

const TOKEN_KEY = 'lp_token';
export const ACTIVE_COMPANY_KEY = 'lp_active_company_id';
const SAFE_OPERATION_OUTCOMES = new Set([
  'authorization_checking',
  'no_credential_change',
  'credential_change_unconfirmed',
  'credential_saved_refresh_incomplete',
  'fully_reconciled',
  'oauth_callback_in_progress',
  'reauthorization_required',
  'refresh_in_progress',
  'refresh_incomplete',
]);
const MAX_RETRY_AFTER_SECONDS = 30;
// OGC-450: a Meta ad-account throttle legitimately asks for a wait of up to a
// day (OGC-448); every other retry-after stays inside the short bound.
const LONG_RETRY_AFTER_CODES = new Map([
  ['curated_publish_meta_rate_limited', 24 * 60 * 60],
  ['campaign_delete_incomplete', 24 * 60 * 60],
  ['campaign_archive_pause_rate_limited', 24 * 60 * 60],
]);
const MAX_PAGE_ASSIGNMENT_DEPENDENCY_COUNT = 10000;
export const CURATED_CAMPAIGN_ARCHIVE_FILTERS = Object.freeze(['exclude', 'include', 'only']);
export const CURATED_CAMPAIGN_LIST_PAGE_SIZE = 50;

export function getToken() { return localStorage.getItem(TOKEN_KEY); }
export function setToken(t) { localStorage.setItem(TOKEN_KEY, t); }
export function clearToken() {
  clearZapierLeadsContext();
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem('lp_user');
  localStorage.removeItem(ACTIVE_COMPANY_KEY);
  // OGC-527: an onboarding Meta continuation never survives sign-out.
  try { sessionStorage.removeItem('lpam:onboarding-meta-continuation'); } catch { /* unavailable */ }
}

export function logout() {
  clearToken();
  if (typeof window !== 'undefined' && window.location) {
    window.location.href = '/login';
  }
}

function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const ORIGINAL_UNPROVABLE_CONTINUITY_CAUSE = 'meta_campaign_credential_continuity_original_unprovable';

export class ApiError extends Error {
  constructor({
    status,
    statusText,
    code,
    safeMessage,
    readiness,
    blockedReasons,
    outcome,
    credentialChanged,
    retryable,
    retryAfterSeconds,
    newAttemptAllowed,
    dependencyCounts,
    retirableUnusedSetup,
    localArchiveAvailable,
    activeArchiveAvailable,
    cause,
  }) {
    const statusLabel = status ? `${status} ${statusText || ''}`.trim() : 'Request failed';
    const codeLabel = code ? ` ${code}` : '';
    super(`${statusLabel}${codeLabel}: ${safeMessage || 'Request failed safely.'}`);
    this.name = 'ApiError';
    this.status = status || 0;
    this.statusText = statusText || '';
    this.code = code || null;
    this.safeMessage = safeMessage || 'Request failed safely.';
    if (SAFE_OPERATION_OUTCOMES.has(outcome)) this.outcome = outcome;
    if (typeof credentialChanged === 'boolean') this.credentialChanged = credentialChanged;
    if (retryable === true) this.retryable = true;
    // OGC-455: the server decides whether the local-only archive is on offer.
    // Only the literal true carries, so an absent or odd value never invents it.
    if (localArchiveAvailable === true) this.localArchiveAvailable = true;
    // OGC-493: an active campaign may leave on the owner's typed confirmation
    // that they paused it in Meta; only the server offers it.
    if (activeArchiveAvailable === true) this.activeArchiveAvailable = true;
    // OGC-488: an archive or delete refusal whose campaign's original Meta
    // login can no longer be confirmed names that, so the copy can stop
    // suggesting a reconnect. Only that exact cause carries.
    if (cause === ORIGINAL_UNPROVABLE_CONTINUITY_CAUSE) this.continuityCause = cause;
    if (typeof newAttemptAllowed === 'boolean') this.newAttemptAllowed = newAttemptAllowed;
    if (Number.isSafeInteger(retryAfterSeconds) && retryAfterSeconds > 0) {
      this.retryAfterSeconds = Math.min(retryAfterSeconds, LONG_RETRY_AFTER_CODES.get(this.code) ?? MAX_RETRY_AFTER_SECONDS);
    }
    if (readiness) this.readiness = sanitizeCampaignLaunchReadiness(readiness);
    const safeDependencyCounts = sanitizeDependencyCounts(dependencyCounts);
    if (safeDependencyCounts) this.dependencyCounts = safeDependencyCounts;
    // OGC-529: the server alone decides whether retiring an unused company
    // setup can clear the block; only the literal true carries.
    if (safeDependencyCounts && retirableUnusedSetup === true) this.retirableUnusedSetup = true;
    if (Array.isArray(blockedReasons)) {
      this.blockedReasons = blockedReasons
        .filter((reason) => typeof reason?.code === 'string')
        .slice(0, 20)
        .map((reason) => ({ code: reason.code }));
    }
  }
}

function sanitizePageAssignmentDependencyCounts(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const fields = ['activeForms', 'formOperations', 'campaigns', 'formRecoveries'];
  if (fields.some((field) => (
    !Number.isSafeInteger(value[field])
    || value[field] < 0
    || value[field] > MAX_PAGE_ASSIGNMENT_DEPENDENCY_COUNT
  ))) return null;
  // OGC-529: company setups are reported separately when the server sends them.
  const companySetups = Number.isSafeInteger(value.companySetups)
    && value.companySetups >= 0
    && value.companySetups <= MAX_PAGE_ASSIGNMENT_DEPENDENCY_COUNT
    ? value.companySetups
    : 0;
  return Object.freeze({
    ...Object.fromEntries(fields.map((field) => [field, value[field]])),
    companySetups,
  });
}

function sanitizeMetaRemovalDependencyCounts(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const fields = [
    'protectedDrafts',
    'forms',
    'publishedCampaigns',
    'unsettledOperations',
  ];
  const optional = Object.hasOwn(value, 'companySetups') ? ['companySetups'] : [];
  const allowed = [...fields, ...optional];
  if (Object.keys(value).length !== allowed.length || allowed.some((field) => (
    !Object.hasOwn(value, field)
    || !Number.isSafeInteger(value[field])
    || value[field] < 0
    || value[field] > MAX_PAGE_ASSIGNMENT_DEPENDENCY_COUNT
  ))) return null;
  return Object.freeze({
    ...Object.fromEntries(fields.map((field) => [field, value[field]])),
    companySetups: optional.length ? value.companySetups : 0,
  });
}

function sanitizeDependencyCounts(value) {
  return sanitizePageAssignmentDependencyCounts(value)
    || sanitizeMetaRemovalDependencyCounts(value);
}

async function responseError(res) {
  const contentType = res.headers.get('content-type') || '';
  const text = await res.text().catch(() => '');
  let payload = null;
  if (contentType.includes('application/json') && text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  const rawError = payload?.error;
  const code =
    typeof rawError === 'string'
      ? rawError
      : typeof rawError?.code === 'string'
        ? rawError.code
        : typeof payload?.code === 'string'
          ? payload.code
          : null;

  return new ApiError({
    status: res.status,
    statusText: res.statusText,
    code,
    safeMessage: 'Request failed safely.',
    readiness: payload?.readiness,
    blockedReasons: payload?.blockedReasons,
    outcome: payload?.outcome,
    credentialChanged: payload?.credentialChanged,
    retryable: payload?.retryable,
    retryAfterSeconds: payload?.retryAfterSeconds,
    newAttemptAllowed: payload?.newAttemptAllowed,
    dependencyCounts: payload?.dependencyCounts,
    retirableUnusedSetup: payload?.retirableUnusedSetup,
    localArchiveAvailable: payload?.localArchiveAvailable,
    activeArchiveAvailable: payload?.activeArchiveAvailable,
    cause: payload?.cause,
  });
}

async function request(path, {
  method = 'GET',
  body,
  headers,
  rawBody = false,
  signal,
  redirectOnUnauthorized = true,
} = {}) {
  const res = await fetch(path, {
    method,
    headers: {
      ...(rawBody ? {} : { 'Content-Type': 'application/json' }),
      ...authHeaders(),
      ...(headers || {}),
    },
    body: body == null ? undefined : rawBody ? body : JSON.stringify(body),
    signal,
  });
  // Background reads (no user action) report 401 without leaving the page, so
  // an expired session never discards unsaved edits on its own.
  if (res.status === 401 && !redirectOnUnauthorized) {
    throw new ApiError({
      status: 401,
      statusText: res.statusText,
      code: 'unauthorized',
      safeMessage: 'Sign in again to continue.',
    });
  }
  if (res.status === 401) {
    clearToken();
    if (typeof window !== 'undefined' && window.location) {
      window.location.href = loginHrefForCurrentLocation();
    }
    throw new ApiError({
      status: 401,
      statusText: res.statusText,
      code: 'unauthorized',
      safeMessage: 'Sign in again to continue.',
    });
  }
  if (!res.ok) {
    throw await responseError(res);
  }
  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json') ? res.json() : res.text();
}

export const api = {
  get:   (p, options) => request(p, options),
  post:  (p, body, options) => request(p, { ...(options || {}), method: 'POST', body }),
  del:   (p, options) => request(p, { ...(options || {}), method: 'DELETE' }),
  patch: (p, body, options) => request(p, { ...(options || {}), method: 'PATCH', body }),
  put:   (p, body, options) => request(p, { ...(options || {}), method: 'PUT', body }),
  upload: (p, body, options) => request(p, {
    ...(options || {}),
    method: 'POST',
    body,
    rawBody: true,
  }),

  // SSE — EventSource can't set custom headers, so we pass the token as a
  // query param. The backend already reads it from the Authorization header
  // via requireAuth, but SSE is sent after the initial auth check on the GET,
  // so we don't need to authenticate the stream itself — just the initial GET.
  stream(path, { onMessage, onError } = {}) {
    const token = getToken();
    const url = token ? `${path}${path.includes('?') ? '&' : '?'}_t=${token}` : path;
    const es = new EventSource(url);
    if (onMessage) es.onmessage = (e) => {
      try { onMessage(JSON.parse(e.data)); } catch { onMessage(e.data); }
    };
    if (onError) es.onerror = onError;
    return es;
  },
};

export const platformRegistrationSettingsApi = {
  get: options => api.get('/api/saas/platform/registration-settings', options),
  update: (body, options) => api.patch('/api/saas/platform/registration-settings', registrationSettingsBody(body), options),
};

export const platformZapierSettingsApi = {
  get: options => api.get('/api/saas/platform/zapier-settings', options),
  update: (body, options) => api.patch('/api/saas/platform/zapier-settings', platformZapierSettingsBody(body), options),
};

function curatedCampaignTenantOptions(companyId, options = {}) {
  return {
    ...options,
    headers: {
      ...(options.headers || {}),
      'x-company-id': companyId,
    },
  };
}

function curatedCampaignPath(campaignId, suffix = '') {
  return `/api/saas/campaigns/${encodeURIComponent(String(campaignId || ''))}${suffix}`;
}

export function curatedCampaignListPath(archived = 'exclude', {
  cursor = '',
  limit = CURATED_CAMPAIGN_LIST_PAGE_SIZE,
  search = '', renovationTypeId = '', status = '', sort = '',
} = {}) {
  const filter = CURATED_CAMPAIGN_ARCHIVE_FILTERS.includes(archived) ? archived : 'exclude';
  const params = new URLSearchParams({
    archived: filter,
    limit: String(limit),
  });
  if (typeof cursor === 'string' && cursor) params.set('cursor', cursor);
  for (const [key, value] of Object.entries({ search, renovationTypeId, status, sort })) {
    if (typeof value === 'string' && value) params.set(key, value);
  }
  return `/api/saas/campaigns?${params.toString()}`;
}

function curatedCampaignMutationBody({
  expectedRevision,
  idempotencyKey,
  confirmed,
  confirmationName,
  acknowledgeUnobservableArchive,
  acknowledgePausedInMeta,
} = {}, { archive = false, deletion = false } = {}) {
  return {
    expectedRevision,
    idempotencyKey,
    ...(archive ? { acknowledgeProviderDelivery: true } : {}),
    // OGC-455: sent only when the owner has accepted an archive that changes
    // nothing in Meta. Its absence is what keeps that archive out of reach.
    ...(archive && acknowledgeUnobservableArchive === true
      ? { acknowledgeUnobservableArchive: true }
      : {}),
    // OGC-493: the owner's statement that they paused it in Meta, with the
    // typed name the server re-checks. Only alongside the unobservable
    // acknowledgement, and only as a literal true.
    ...(archive && acknowledgeUnobservableArchive === true && acknowledgePausedInMeta === true
      && typeof confirmationName === 'string'
      ? { acknowledgePausedInMeta: true, confirmationName }
      : {}),
    ...(deletion && confirmed === true ? { confirmed: true } : {}),
    // OGC-452: the server re-checks the typed name against the stored one.
    ...(deletion && typeof confirmationName === 'string' ? { confirmationName } : {}),
  };
}

/** Exact browser contract for tenant-scoped OGC-197 campaign management. */
export const curatedCampaignManagementApi = Object.freeze({
  list({
    companyId,
    archived = 'exclude',
    cursor = '',
    limit = CURATED_CAMPAIGN_LIST_PAGE_SIZE,
    search = '', renovationTypeId = '', status = '', sort = '',
    signal,
  } = {}) {
    return api.get(
      curatedCampaignListPath(archived, { cursor, limit, search, renovationTypeId, status, sort }),
      curatedCampaignTenantOptions(companyId, { signal }),
    );
  },
  get({ companyId, campaignId, signal } = {}) {
    return api.get(
      curatedCampaignPath(campaignId, '/management'),
      curatedCampaignTenantOptions(companyId, { signal }),
    );
  },
  archive(input = {}) {
    return api.post(
      curatedCampaignPath(input.campaignId, '/archive'),
      curatedCampaignMutationBody(input, { archive: true }),
      curatedCampaignTenantOptions(input.companyId),
    );
  },
  restore(input = {}) {
    return api.post(
      curatedCampaignPath(input.campaignId, '/restore'),
      curatedCampaignMutationBody(input),
      curatedCampaignTenantOptions(input.companyId),
    );
  },
  delete(input = {}) {
    return api.del(
      curatedCampaignPath(input.campaignId),
      curatedCampaignTenantOptions(input.companyId, {
        body: curatedCampaignMutationBody(input, { deletion: true }),
      }),
    );
  },
});
